import { api, getApiError } from './api';
import { mapUser } from './mappers';
import { storage } from '../utils/storage';
import type { User } from '../types';

type _Unused = unknown; // eslint

interface AuthResult {
  user: User;
  token: string;
}

export const authService = {
  /**
   * Login with email or username + password.
   * After a successful token exchange, fetches the full employee profile.
   */
  async login(emailOrUsername: string, password: string): Promise<AuthResult> {
    const isEmail = emailOrUsername.includes('@');
    const credentials = isEmail
      ? { email: emailOrUsername, password }
      : { username: emailOrUsername, password };

    try {
      const { data: authData } = await api.post<{ data: string }>('/auth', credentials);
      const token = authData.data;
      storage.setToken(token);

      // Fetch full employee profile
      const { data: profileData } = await api.get<{ data: Record<string, unknown> }>('/me/profile');
      const user = mapUser(profileData.data);
      storage.setUser(user);

      return { user, token };
    } catch (err) {
      storage.removeToken();
      throw new Error(getApiError(err));
    }
  },

  /** Employee accounts are created by HR administrators — self-registration is not supported. */
  async register(): Promise<AuthResult> {
    throw new Error('Self-registration is not available. Please contact your HR administrator to obtain your account credentials.');
  },

  async logout(): Promise<void> {
    try {
      await api.get('/auth/invalidate');
    } finally {
      storage.removeToken();
      storage.removeUser();
    }
  },

  /** Refresh the stored user profile from the API (call after boot if token exists). */
  async refreshProfile(): Promise<User | null> {
    if (!storage.getToken()) return null;
    try {
      const { data } = await api.get<{ data: Record<string, unknown> }>('/me/profile');
      const user = mapUser(data.data);
      storage.setUser(user);
      return user;
    } catch {
      return storage.getUser();
    }
  },

  getCurrentUser(): User | null {
    return storage.getUser();
  },

  isAuthenticated(): boolean {
    return !!storage.getToken() && !!storage.getUser();
  },
};
